const PAYMENT_METHOD_HANDLER_KEY = '__heychargePaymentMethodHandler'
const PAYMENT_METHOD_CALLBACK_KEY = '__heychargeAfterPayment'
const PAYMENT_METHOD_SELECTED_METHOD_KEY = '__heychargeSelectedMethod'

export default {
    /**
     * 初始化 Stripe 支付客户端
     * @param {Object} params - 初始化参数
     * @returns {Object} Stripe paymentRequest 对象
     */
    async initPayment ({
        selectedMethod,
        billingRule,
        repaymentInfo,
        stripe,
        selectedSubscription
    }) {
        // card 方式不需要初始化 paymentClient
        if (selectedMethod === 'card') {
            return null
        }

        console.log('initPayment params:', { selectedMethod, billingRule, repaymentInfo, selectedSubscription });
        const hasRepayment =
            !!repaymentInfo &&
            repaymentInfo.have_unpaid &&
            repaymentInfo.currency

        let currency = billingRule?.currency
        let baseAmount = Number(billingRule?.pre_auth_amount)
        let label = 'Preauthorize Amount'
        console.log('Initial currency from billingRule:', currency);

        if (selectedSubscription?.id) {
            currency = selectedSubscription.currency || billingRule?.currency
            baseAmount = Number(selectedSubscription.price)
            label = 'Subscription Amount'
            console.log('Currency updated from subscription:', currency);
        } else if (hasRepayment) {
            currency = repaymentInfo.currency
            baseAmount = Number(repaymentInfo.unpaid_fee)
            label = 'Total Amount'
            console.log('Currency updated from repayment:', currency);
        }
        
        console.log('Final currency used for paymentRequest:', currency);
        
        const paymentRequestConfig = {
            country: 'US',
            currency: (currency || '').toLowerCase(),
            total: {
                label,
                amount: this.getAmountBasedOnCurrency(
                    (currency || '').toLowerCase(),
                    baseAmount
                )
            },
            requestPayerName: false,
            requestPayerEmail: false
        }

        const paymentRequest = stripe.paymentRequest({
            ...paymentRequestConfig,
            disableWallets:
                (selectedMethod === 'web_apple_pay' || selectedMethod === 'apple_pay')
                    ? ['link', 'googlePay']
                    : ['link', 'applePay']
        })

        return paymentRequest
    },

    /**
     * 检查支付方式可用性
     * @param {Object} params - 检查参数
     * @returns {Object} { available: Boolean, reason: String }
     */
    async checkAvailability ({ selectedMethod, paymentClient }) {
        // card 方式不需要检查可用性，始终可用（只要 Stripe 初始化成功）
        if (selectedMethod === 'card') {
            return { available: true }
        }

        try {
            const result = await paymentClient.canMakePayment()
            if (result) {
                return { available: true, reason: `Available: ${JSON.stringify(result)}` }
            }
            return { available: false, reason: 'Device not supported or no card in wallet' }
        } catch (err) {
            console.error(`Stripe ${selectedMethod} 可用性检查失败:`, err)
            return { available: false, reason: err.message }
        }
    },

    /**
     * 处理支付流程
     * @param {Object} params - 支付参数
     */
    async handlePayment ({ paymentClient, selectedMethod, afterPayment }) {
        paymentClient[PAYMENT_METHOD_CALLBACK_KEY] = afterPayment
        paymentClient[PAYMENT_METHOD_SELECTED_METHOD_KEY] = selectedMethod

        if (!paymentClient[PAYMENT_METHOD_HANDLER_KEY]) {
            paymentClient[PAYMENT_METHOD_HANDLER_KEY] = ev => {
                const currentAfterPayment = paymentClient[PAYMENT_METHOD_CALLBACK_KEY]
                const currentSelectedMethod =
                    paymentClient[PAYMENT_METHOD_SELECTED_METHOD_KEY]

                if (typeof currentAfterPayment === 'function') {
                    currentAfterPayment({
                        ev,
                        selectedMethod: currentSelectedMethod
                    })
                }
            }
            paymentClient.on(
                'paymentmethod',
                paymentClient[PAYMENT_METHOD_HANDLER_KEY]
            )
        }

        try {
            paymentClient.show()
        } catch {
            throw new Error('此支付方式不可用！')
        }
    },

    /**
     * 根据货币获取金额（转换为分）
     * @param {String} currency - 货币代码
     * @param {Number} amount - 金额
     * @returns {Number} 转换后的金额
     */
    getAmountBasedOnCurrency (currency, amount) {
        const MinimumChargeAmounts = {
            usd: 50,
            aed: 200,
            aud: 50,
            bgn: 100,
            brl: 50,
            cad: 50,
            chf: 50,
            czk: 1500,
            dkk: 250,
            eur: 50,
            gbp: 30,
            hkd: 400,
            hrk: 50,
            huf: 17500,
            inr: 50,
            jpy: 5000,
            jmd: 8000,
            mxn: 1000,
            myr: 200,
            nok: 300,
            nzd: 50,
            pln: 200,
            ron: 200,
            sek: 300,
            sgd: 50,
            thb: 1000,
            zar: 1000
        }

        const normalizedAmount = Number.isFinite(amount) ? amount : 0
        if (normalizedAmount <= 0) {
            const minimum = MinimumChargeAmounts[currency]
            return Number.isFinite(minimum) ? minimum : 0
        }
        return Math.round(normalizedAmount * 100)
    },

    /**
     * 初始化 Stripe 实例（假设由父组件传入）
     * @returns {Object} Stripe 实例
     */
    async init () {
        // 假设 config.stripe 已由父组件（如 PaymentSelect.vue）传入
        return this.stripe
    }
}
